import { useEffect, useState } from 'react';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { Panel } from '../../components/Panel';
import { formatDateTime } from '../../lib/format';
import { humanMessage } from '../../lib/errors';
import { isPlatformAuthenticatorAvailable } from './webauthn';
import { usePasskeys } from './usePasskeys';

/**
 * Керування ключами входу — у кабінеті батьків, а не на екрані входу.
 * Додати ключ можна ЛИШЕ з уже відкритої сесії: інакше кнопка «додати» сама
 * стала б способом зайти без пароля.
 *
 * Це панель БАТЬКА: тут повний список із датами, бо ключів у нього може бути
 * кілька (телефон, ноутбук, апаратний). У дитини свій, значно простіший
 * екран — див. QuickLoginCard.
 */
export function PasskeyPanel(): React.JSX.Element {
  const [platformReady, setPlatformReady] = useState<boolean | null>(null);
  const passkeys = usePasskeys();

  useEffect(() => {
    void isPlatformAuthenticatorAvailable().then(setPlatformReady);
  }, []);

  const { add, remove } = passkeys;

  return (
    <Panel className="p-5">
      {/*
        У назві обидва світи: на iPhone це Face ID, на Android — відбиток або
        розблокування обличчям. Механізм той самий (WebAuthn), і називати його
        лише «Face ID» означало б, що половина пристроїв у домі вважає, що це
        не про них.
      */}
      <h3 className="text-base font-black">Вхід за відбитком або Face ID</h3>
      <p className="mt-1 text-xs font-semibold text-white/55">
        Ключ живе в самому пристрої й відкривається відбитком, Face ID або кодом блокування — на
        сервер він не потрапляє. PIN і пароль лишаються на місці: це швидкий вхід, а не заміна.
        Дітям те саме доступно з їхнього екрана — ключі в кожного свої.
      </p>

      {platformReady === false ? (
        <p className="mt-3 text-xs font-bold text-[var(--lk-gold)]">
          Цей браузер не пропонує вбудований відбиток або Face ID. Ключ усе одно можна додати —
          телефоном поруч або апаратним ключем.
        </p>
      ) : null}

      {passkeys.isError ? (
        <p className="mt-3 text-sm font-bold text-[var(--lk-red)]" role="alert">
          {humanMessage(passkeys.error)}
        </p>
      ) : null}

      {passkeys.list.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-2">
          {passkeys.list.map((key) => (
            <li key={key.id} className="lk-panel-tight flex flex-wrap items-center gap-3 p-3">
              <Icon name="fingerprint" size={20} />
              <div className="min-w-40 flex-1">
                <p className="text-sm font-black">{key.label}</p>
                <p className="text-xs font-semibold text-white/50">
                  доданий {formatDateTime(key.createdAt)}
                  {key.lastUsedAt ? ` · останній вхід ${formatDateTime(key.lastUsedAt)}` : ' · ще не використаний'}
                </p>
              </div>
              <button
                type="button"
                className="lk-chip cursor-pointer"
                disabled={remove.isPending}
                onClick={() => remove.mutate(key.id)}
              >
                прибрати
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-xs font-semibold text-white/40">
          {passkeys.isLoading ? 'Читаємо ключі…' : 'Ключів ще немає.'}
        </p>
      )}

      <Button
        tone="ghost"
        mute
        className="mt-4"
        loading={add.isPending}
        disabled={add.isPending}
        onClick={() => add.mutate()}
      >
        <Icon name="fingerprint" size={20} />
        Додати ключ на цьому пристрої
      </Button>

      {add.isError ? (
        <p className="mt-3 text-sm font-bold text-[var(--lk-red)]" role="alert">
          {humanMessage(add.error)}
        </p>
      ) : null}
      {add.isSuccess ? (
        <p className="mt-3 text-sm font-bold text-[var(--lk-green)]" role="status">
          Готово. Тепер на екрані входу цього пристрою зʼявиться кнопка «Face ID / відбиток».
        </p>
      ) : null}
      {remove.isError ? (
        <p className="mt-3 text-sm font-bold text-[var(--lk-red)]" role="alert">
          {humanMessage(remove.error)}
        </p>
      ) : null}
    </Panel>
  );
}
